import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../services/apiConfig";
import { anonymousQuizStorage, publicApi } from "../services/publicApi";
import "../styles/login.css";

const Login = () => {
  const [step, setStep] = useState("email"); // 'email' or 'verify'
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const navigate = useNavigate();
  const location = useLocation();

  // Extract returnTo path from URL if present
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get("returnTo") || "/settings";

  // Check if user is already logged in
  useEffect(() => {
    const userEmail = localStorage.getItem("userEmail");
    const userVerified = localStorage.getItem("userVerified") === "true";

    if (userEmail && userVerified) {
      // User already logged in, redirect to returnTo path or settings
      navigate(returnTo);
    }
  }, [navigate, returnTo]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!isValidEmail(email)) {
      showMessage("error", "Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      // Use the login-specific verification endpoint
      const formData = new FormData();
      formData.append("email", email);

      // Use the dedicated login verification endpoint
      const response = await api.post("/api/send-login-verification", formData);

      if (response.data.message) {
        showMessage("success", response.data.message);
        setStep("verify");
      }
    } catch (error) {
      showMessage(
        "error",
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();

    if (!verificationCode) {
      showMessage("error", "Please enter the verification code");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("email", email);
      formData.append("code", verificationCode);
      formData.append("template_type", "login");

      const response = await api.post("/api/verify", formData);

      if (response.data.message && response.data.message.includes("verified")) {
        showMessage("success", "Login successful!");

        // Save user data in localStorage
        localStorage.setItem("userEmail", email);
        localStorage.setItem("userVerified", "true");

        // Sync any pending anonymous quiz results
        const pendingResults = anonymousQuizStorage.get();
        if (pendingResults) {
          try {
            await publicApi.syncQuizResults(email, pendingResults);
            anonymousQuizStorage.clear();
          } catch (syncErr) {
            console.error("Failed to sync pending quiz results:", syncErr);
            // Don't block login flow if sync fails
          }
        }

        setTimeout(() => {
          // Redirect to returnTo path or settings page after successful login
          navigate(returnTo);
        }, 1500);
      } else {
        showMessage(
          "error",
          response.data.message || "Invalid verification code"
        );
      }
    } catch (error) {
      showMessage(
        "error",
        error.response?.data?.message || "An error occurred. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => {
      setMessage({ type: "", text: "" });
    }, 5000);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h2>
            {step === "email" ? "Login to PrepLetter" : "Verify Your Email"}
          </h2>
          <p>
            {step === "email"
              ? "Enter your email to receive a verification code"
              : "Enter the verification code sent to your email"}
          </p>
        </div>

        {step === "email" ? (
          <form onSubmit={handleEmailSubmit} className="login-form">
            <div className="form-group">
              <div className="input-icon-wrapper">
                <i className="bi bi-envelope"></i>
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {message.text && (
              <div className={`alert ${message.type}`}>{message.text}</div>
            )}

            <button type="submit" className="login-button" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div className="spinner"></div>
                  <span>Sending...</span>
                </>
              ) : (
                <span>Continue</span>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerificationSubmit} className="login-form">
            <div className="form-group">
              <div className="input-icon-wrapper">
                <i className="bi bi-shield-lock"></i>
                <input
                  type="text"
                  placeholder="6-digit verification code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  maxLength={6}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {message.text && (
              <div className={`alert ${message.type}`}>{message.text}</div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="back-button"
                onClick={() => {
                  setStep("email");
                  setVerificationCode("");
                  setMessage({ type: "", text: "" });
                }}
                disabled={isLoading}
              >
                Back
              </button>

              <button
                type="submit"
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <span>Verify & Login</span>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
